import React, { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MessagingContext from '../../providers/Messaging/Messaging.context';
import EmptyScreen from 'components/EmptyScreen';
import classNames from 'classnames';
import BuyerInfo from './BuyerInfo';
import { Button } from 'ncoded-component-library';
import ArrowleftIcon from 'icons/Arrowleft.icon';
import ListingInformation from './ListingInformation';
import SellerInfo from './SellerInfo';
import GroupMembers from './GroupMembers';
import NdaSigningProgress from '../NdaSigningProgress';
import { getImgUrl } from 'components/ResolveImage/utils';
import PortfolioDataRoomNodes from '../../../../components/PortfolioDataRoomNodes';
import { Link } from 'react-router-dom';
import LinkToIcon from 'icons/LinkTo.icon';
import { checkHasListing, getSellingPortfolio } from 'utils';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import { NewFilesEventData } from 'services/socketService/socketEventData';
import BuyerNdaCard from '../../../../components/BuyerNdaCard';
import { UpdateLeadNdaProps } from 'hooks/useNdaStatus';
import MessageFiles from 'components/MessageFiles';
import { NEW_WINDOW_PROPS } from 'data/global';

import './ContactPreview.styles.scss';
import './ContactPreview.styles.responsive.scss';

type ProfilePreviewProps = {
  className?: string;
};

const ProfilePreview: React.FC<ProfilePreviewProps> = (props) => {
  const { className } = props;

  const classes = classNames('contact-preview', className);

  const { t } = useTranslation();

  const {
    sessionId,
    currentSession,
    loadingSession,
    userSide,
    setContactOpen,
    updateCurrentSession,
  } = useContext(MessagingContext);

  const {
    isDirectMessage,
    interlocutor,
    isExternalCommunication,
    isRepresentingSellSide,
    isGroupChat,
    sellRepresentatives,
    buyRepresentatives,
    deal,
    dataRoomNodes,
    enquiryQuestions,
    signingStatus,
    signedDate,
    envelopeId,
    manuallySetNDAStatus,
    rejectionReason,
    envelopeTemplateName,
    leadId,
  } = currentSession || {};

  const {
    _id: dealRoomId,
    dataRoomId,
    listing,
    companyName,
    companyLogo,
    name: dealName,
    portfolioName,
    userId: ownerId,
  } = deal || {};

  const isInterlocutorSeller = !isDirectMessage && !isRepresentingSellSide;
  const showSellerPanel = !!companyName;

  const RenderedMessageFiles = useMemo(
    () => (
      <MessageFiles
        sessionId={sessionId}
        dataRoomId={dataRoomId}
        dataRoomName={dealName}
        ownerId={ownerId}
        userSide={userSide}
      />
    ),
    [dataRoomId, dealName, ownerId, sessionId, userSide],
  );

  const updateNdaProps = useCallback(
    (leadNdaProps: UpdateLeadNdaProps) => updateCurrentSession(leadNdaProps),
    [updateCurrentSession],
  );

  const newFiles = useCallback(
    ({ dataRoomId: payloadDataRoomId, dataRoomNodes }: NewFilesEventData) => {
      if (payloadDataRoomId !== dataRoomId) return;
      updateCurrentSession({ dataRoomNodes });
    },
    [dataRoomId, updateCurrentSession],
  );

  useSocketEvent(LISTEN_EVENTS.NEW_FILES, newFiles);

  if (loadingSession) return null;

  if (!sessionId || !currentSession)
    return (
      <EmptyScreen
        className={classNames('contact-preview__empty-screen', className)}
        content={t('selectPersonToSeeProfile')}
      />
    );

  return (
    <div className={classes}>
      <div className="contact-preview__actions">
        <Button
          variant="icon"
          className="chat-arrow"
          icon={ArrowleftIcon}
          onClick={() => setContactOpen((old) => !old)}
          aria-label="Back to chat."
        />
      </div>

      {isDirectMessage ? (
        <>
          <BuyerInfo buyer={interlocutor} />
          {RenderedMessageFiles}
        </>
      ) : (
        <>
          {isInterlocutorSeller ? (
            showSellerPanel && (
              <SellerInfo
                title={companyName}
                imageUrl={getImgUrl(companyLogo?.url, '240')}
              />
            )
          ) : isGroupChat ? (
            <GroupMembers
              sellRepresentatives={sellRepresentatives}
              buyRepresentatives={buyRepresentatives}
            />
          ) : (
            <BuyerInfo
              buyer={interlocutor}
              enquiryQuestions={enquiryQuestions}
            />
          )}

          {!isGroupChat &&
            (!isInterlocutorSeller ? (
              <NdaSigningProgress />
            ) : (
              <BuyerNdaCard
                leadId={leadId}
                signingStatus={signingStatus}
                envelopeId={envelopeId}
                signedDate={signedDate}
                manuallySetNDAStatus={manuallySetNDAStatus}
                envelopeTemplateName={envelopeTemplateName}
                rejectionReason={rejectionReason}
                updateNdaProps={updateNdaProps}
              />
            ))}

          {RenderedMessageFiles}

          {isInterlocutorSeller && (
            <PortfolioDataRoomNodes
              dataRoomId={dataRoomId}
              dataRoomNodes={dataRoomNodes}
              dataRoomName={dealName}
              ownerId={ownerId}
              userSide={userSide}
              dealRoomId={dealRoomId}
            />
          )}

          {isInterlocutorSeller &&
            checkHasListing(listing?.dealRoom?.listingStage) && (
              <ListingInformation
                listing={listing}
                isInterlocutorSeller={isInterlocutorSeller}
                isExternalCommunication={isExternalCommunication}
              />
            )}

          {isExternalCommunication && (
            <div className="external-communicatator-info">
              <p>{portfolioName || dealName}</p>
              <Link to={getSellingPortfolio(dealRoomId)} {...NEW_WINDOW_PROPS}>
                <LinkToIcon />
                {t('portfolio')}
              </Link>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProfilePreview;
